import React, { useEffect, useMemo, useState } from "react";
import { api, formatError } from "@/lib/api";
import { toast } from "sonner";
import { PageHeader } from "@/components/primitives";
import { Plus, X, Trash, PaperPlaneTilt, Robot } from "@phosphor-icons/react";

const BLANK_LINE = { account_code: "", debit: "", credit: "", description: "" };
const BLANK_FORM = {
  date: new Date().toISOString().slice(0, 10),
  memo: "",
  reference: "",
  lines: [ { ...BLANK_LINE }, { ...BLANK_LINE } ],
};

function fmtZMW(n) {
  const v = Number(n || 0);
  return v.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}
function fmtDate(iso) {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleDateString(undefined, { day: "2-digit", month: "short", year: "numeric" }); }
  catch { return iso; }
}

export default function Journals() {
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [accounts, setAccounts] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(BLANK_FORM);
  const [busy, setBusy] = useState(false);
  const [detail, setDetail] = useState(null);

  async function load() {
    setLoading(true);
    try {
      const [j, a] = await Promise.all([
        api.get("/gl/journals?limit=100"),
        api.get("/gl/accounts"),
      ]);
      setRows(j.data.rows || []);
      setTotal(j.data.total || 0);
      setAccounts(a.data || []);
    } catch (e) { toast.error(formatError(e)); }
    setLoading(false);
  }
  useEffect(() => { load(); }, []);

  const totals = useMemo(() => {
    let dr = 0, cr = 0;
    for (const l of form.lines) {
      dr += Number(l.debit || 0);
      cr += Number(l.credit || 0);
    }
    return { dr, cr, balanced: Math.abs(dr - cr) < 0.005 && dr > 0 };
  }, [form.lines]);

  function updateLine(idx, patch) {
    setForm((f) => ({ ...f, lines: f.lines.map((l, i) => i === idx ? { ...l, ...patch } : l) }));
  }
  function addLine() { setForm((f) => ({ ...f, lines: [...f.lines, { ...BLANK_LINE }] })); }
  function removeLine(idx) {
    setForm((f) => ({ ...f, lines: f.lines.length > 2 ? f.lines.filter((_, i) => i !== idx) : f.lines }));
  }

  async function post() {
    if (!form.memo.trim()) return toast.error("Memo is required");
    if (!totals.balanced) return toast.error("Journal must be balanced (Dr = Cr)");
    const lines = form.lines
      .filter((l) => l.account_code && (Number(l.debit || 0) > 0 || Number(l.credit || 0) > 0))
      .map((l) => ({
        account_code: l.account_code,
        debit: Number(l.debit || 0),
        credit: Number(l.credit || 0),
        description: l.description || null,
      }));
    if (lines.length < 2) return toast.error("Need at least two non-zero lines");
    setBusy(true);
    try {
      const { data } = await api.post("/gl/journals", {
        date: form.date, memo: form.memo, reference: form.reference || null, lines,
      });
      toast.success(`Posted ${data.number}`);
      setOpen(false);
      setForm(BLANK_FORM);
      await load();
    } catch (e) { toast.error(formatError(e)); }
    setBusy(false);
  }

  return (
    <div data-testid="journals-page">
      <PageHeader
        overline="Finance / GL"
        title="Journals"
        subtitle="Every posting to the general ledger — manual entries and system auto-postings."
        testId="journals-header"
      />

      <div className="phizo-card p-4 mb-4 flex items-center gap-3 flex-wrap">
        <div className="text-xs text-[#71717a] mono">{total.toLocaleString()} journals posted</div>
        <button
          type="button"
          onClick={() => { setForm(BLANK_FORM); setOpen(true); }}
          data-testid="journal-new-btn"
          className="ml-auto bg-black text-white px-4 py-1.5 text-xs font-medium flex items-center gap-1.5 hover:bg-[#27272a]"
        >
          <Plus size={12} weight="bold" /> New journal
        </button>
      </div>

      <div className="phizo-card overflow-x-auto" data-testid="journals-table">
        <table className="w-full text-xs">
          <thead className="bg-[#fafafa] border-b border-[#e4e4e7]">
            <tr className="text-left">
              <th className="px-3 py-2 phizo-label text-[9px]">Number</th>
              <th className="px-3 py-2 phizo-label text-[9px]">Date</th>
              <th className="px-3 py-2 phizo-label text-[9px]">Memo</th>
              <th className="px-3 py-2 phizo-label text-[9px]">Source</th>
              <th className="px-3 py-2 phizo-label text-[9px] text-right">Debit</th>
              <th className="px-3 py-2 phizo-label text-[9px] text-right">Credit</th>
            </tr>
          </thead>
          <tbody>
            {loading && <tr><td colSpan={6} className="p-6 text-center text-[#71717a]">Loading…</td></tr>}
            {!loading && rows.length === 0 && (
              <tr><td colSpan={6} className="p-6 text-center text-[#71717a]">No journals posted yet.</td></tr>
            )}
            {rows.map((j) => (
              <tr key={j.id}
                  onClick={() => setDetail(j)}
                  className="border-b border-[#f4f4f5] hover:bg-[#fafafa] cursor-pointer"
                  data-testid={`journal-row-${j.number}`}>
                <td className="px-3 py-2 mono font-bold">{j.number}</td>
                <td className="px-3 py-2 whitespace-nowrap">{fmtDate(j.date)}</td>
                <td className="px-3 py-2 max-w-[300px] truncate">{j.memo}</td>
                <td className="px-3 py-2">
                  {j.is_auto ? (
                    <span className="inline-flex items-center gap-1 mono text-[10px] px-1.5 py-0.5 bg-[#dbeafe] text-[#1e40af]">
                      <Robot size={10} weight="bold" /> AUTO
                    </span>
                  ) : (
                    <span className="text-[10px] text-[#71717a]">Manual</span>
                  )}
                  {j.source_label && (
                    <div className="text-[10px] text-[#71717a] mono">{j.source_label}</div>
                  )}
                </td>
                <td className="px-3 py-2 text-right mono">{fmtZMW(j.total_debit)}</td>
                <td className="px-3 py-2 text-right mono">{fmtZMW(j.total_credit)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* New journal modal */}
      {open && (
        <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4"
             onClick={() => setOpen(false)}>
          <div className="bg-white w-full max-w-4xl max-h-[92vh] overflow-y-auto"
               onClick={(e) => e.stopPropagation()}
               data-testid="journal-modal">
            <div className="sticky top-0 bg-white px-5 py-3 border-b border-[#e4e4e7] flex items-center justify-between">
              <div>
                <div className="phizo-label text-[10px] text-[#a1a1aa]">General Ledger</div>
                <h3 className="font-heading text-xl font-black tracking-tighter">Post journal</h3>
              </div>
              <button onClick={() => setOpen(false)} className="p-1 text-[#71717a] hover:text-black">
                <X size={16} weight="bold" />
              </button>
            </div>

            <div className="p-5 space-y-3">
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                <label className="text-xs">
                  <div className="phizo-label text-[10px] mb-1">Date</div>
                  <input type="date" value={form.date}
                         onChange={(e) => setForm({ ...form, date: e.target.value })}
                         className="w-full border border-[#e4e4e7] px-3 py-1.5 text-sm outline-none focus:border-black"
                         data-testid="journal-date"/>
                </label>
                <label className="text-xs sm:col-span-2">
                  <div className="phizo-label text-[10px] mb-1">Memo</div>
                  <input type="text" value={form.memo}
                         onChange={(e) => setForm({ ...form, memo: e.target.value })}
                         placeholder="Why is this entry being made?"
                         className="w-full border border-[#e4e4e7] px-3 py-1.5 text-sm outline-none focus:border-black"
                         data-testid="journal-memo"/>
                </label>
                <label className="text-xs">
                  <div className="phizo-label text-[10px] mb-1">Reference (optional)</div>
                  <input type="text" value={form.reference}
                         onChange={(e) => setForm({ ...form, reference: e.target.value })}
                         placeholder="e.g. INV-001, BANK-STMT-05"
                         className="w-full border border-[#e4e4e7] px-3 py-1.5 text-sm outline-none focus:border-black"
                         data-testid="journal-reference"/>
                </label>
              </div>

              <div className="border border-[#e4e4e7]">
                <table className="w-full text-xs">
                  <thead className="bg-[#fafafa]">
                    <tr className="text-left">
                      <th className="px-2 py-2 phizo-label text-[9px]">Account</th>
                      <th className="px-2 py-2 phizo-label text-[9px] text-right w-32">Debit</th>
                      <th className="px-2 py-2 phizo-label text-[9px] text-right w-32">Credit</th>
                      <th className="px-2 py-2 phizo-label text-[9px]">Description</th>
                      <th className="w-10"></th>
                    </tr>
                  </thead>
                  <tbody>
                    {form.lines.map((l, idx) => (
                      <tr key={idx} className="border-t border-[#f4f4f5]">
                        <td className="px-2 py-1.5">
                          <select value={l.account_code}
                                  onChange={(e) => updateLine(idx, { account_code: e.target.value })}
                                  className="w-full border border-[#e4e4e7] px-2 py-1 text-xs outline-none focus:border-black mono"
                                  data-testid={`journal-line-account-${idx}`}>
                            <option value="">— select account —</option>
                            {accounts.filter((a) => a.is_active).map((a) => (
                              <option key={a.code} value={a.code}>{a.code} — {a.name}</option>
                            ))}
                          </select>
                        </td>
                        <td className="px-2 py-1.5">
                          <input type="number" step="0.01" value={l.debit}
                                 onChange={(e) => updateLine(idx, { debit: e.target.value, credit: "" })}
                                 className="w-full border border-[#e4e4e7] px-2 py-1 text-xs text-right mono outline-none focus:border-black"
                                 data-testid={`journal-line-debit-${idx}`}/>
                        </td>
                        <td className="px-2 py-1.5">
                          <input type="number" step="0.01" value={l.credit}
                                 onChange={(e) => updateLine(idx, { credit: e.target.value, debit: "" })}
                                 className="w-full border border-[#e4e4e7] px-2 py-1 text-xs text-right mono outline-none focus:border-black"
                                 data-testid={`journal-line-credit-${idx}`}/>
                        </td>
                        <td className="px-2 py-1.5">
                          <input type="text" value={l.description}
                                 onChange={(e) => updateLine(idx, { description: e.target.value })}
                                 className="w-full border border-[#e4e4e7] px-2 py-1 text-xs outline-none focus:border-black"/>
                        </td>
                        <td className="text-center">
                          <button type="button" onClick={() => removeLine(idx)}
                                  disabled={form.lines.length <= 2}
                                  className="text-[#71717a] hover:text-[#dc2626] disabled:opacity-30 p-1">
                            <Trash size={12} weight="bold" />
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                  <tfoot className="bg-[#fafafa] border-t-2 border-[#e4e4e7]">
                    <tr>
                      <td className="px-2 py-2 text-xs font-bold text-right">Totals</td>
                      <td className="px-2 py-2 text-xs text-right mono font-bold" data-testid="journal-total-debit">
                        {fmtZMW(totals.dr)}
                      </td>
                      <td className="px-2 py-2 text-xs text-right mono font-bold" data-testid="journal-total-credit">
                        {fmtZMW(totals.cr)}
                      </td>
                      <td colSpan={2} className="px-2 py-2 text-right">
                        {totals.balanced ? (
                          <span className="text-[10px] text-[#166534] font-bold">✓ BALANCED</span>
                        ) : (
                          <span className="text-[10px] text-[#991b1b] font-bold">
                            Diff: {fmtZMW(totals.dr - totals.cr)}
                          </span>
                        )}
                      </td>
                    </tr>
                  </tfoot>
                </table>
              </div>

              <div className="flex items-center gap-2">
                <button type="button" onClick={addLine}
                        className="border border-[#e4e4e7] text-xs px-3 py-1.5 hover:bg-[#f4f4f5]"
                        data-testid="journal-add-line">
                  <Plus size={12} weight="bold" className="inline mr-1" /> Add line
                </button>
              </div>
            </div>

            <div className="px-5 py-3 border-t border-[#e4e4e7] flex justify-end gap-2">
              <button type="button" onClick={() => setOpen(false)}
                      className="border border-[#e4e4e7] px-4 py-1.5 text-xs hover:bg-[#f4f4f5]">
                Cancel
              </button>
              <button type="button" onClick={post}
                      disabled={busy || !totals.balanced}
                      data-testid="journal-post-btn"
                      className="bg-[#002FA7] text-white px-4 py-1.5 text-xs font-medium hover:bg-[#001f7a] disabled:opacity-60 flex items-center gap-1.5">
                <PaperPlaneTilt size={12} weight="bold" />
                {busy ? "Posting…" : "Post journal"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Detail modal */}
      {detail && (
        <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4"
             onClick={() => setDetail(null)}>
          <div className="bg-white w-full max-w-3xl" onClick={(e) => e.stopPropagation()}
               data-testid="journal-detail">
            <div className="px-5 py-3 border-b border-[#e4e4e7] flex justify-between items-start">
              <div>
                <div className="phizo-label text-[10px] text-[#a1a1aa]">
                  Journal · {detail.is_auto ? "system-posted" : "manual"}
                </div>
                <h3 className="font-heading text-xl font-black tracking-tighter">{detail.number}</h3>
                <div className="text-xs text-[#52525b] mt-1">
                  {fmtDate(detail.date)} · {detail.memo}
                  {detail.reference && <span className="mono ml-2">Ref: {detail.reference}</span>}
                </div>
                {detail.source_label && (
                  <div className="mono text-[10px] text-[#1e40af] mt-1">Source: {detail.source_label}</div>
                )}
              </div>
              <button onClick={() => setDetail(null)} className="p-1 text-[#71717a] hover:text-black">
                <X size={16} weight="bold" />
              </button>
            </div>
            <table className="w-full text-xs">
              <thead className="bg-[#fafafa] border-b border-[#e4e4e7]">
                <tr className="text-left">
                  <th className="px-3 py-2 phizo-label text-[9px]">Account</th>
                  <th className="px-3 py-2 phizo-label text-[9px] text-right">Debit</th>
                  <th className="px-3 py-2 phizo-label text-[9px] text-right">Credit</th>
                  <th className="px-3 py-2 phizo-label text-[9px]">Description</th>
                </tr>
              </thead>
              <tbody>
                {(detail.lines || []).map((l, idx) => (
                  <tr key={idx} className="border-b border-[#f4f4f5]">
                    <td className="px-3 py-2 mono">{l.account_code}</td>
                    <td className="px-3 py-2 text-right mono">{Number(l.debit || 0) ? fmtZMW(l.debit) : "—"}</td>
                    <td className="px-3 py-2 text-right mono">{Number(l.credit || 0) ? fmtZMW(l.credit) : "—"}</td>
                    <td className="px-3 py-2 text-[#52525b]">{l.description || "—"}</td>
                  </tr>
                ))}
              </tbody>
              <tfoot className="bg-[#fafafa] border-t-2">
                <tr>
                  <td className="px-3 py-2 text-xs font-bold text-right">Totals</td>
                  <td className="px-3 py-2 text-right mono font-bold">{fmtZMW(detail.total_debit)}</td>
                  <td className="px-3 py-2 text-right mono font-bold">{fmtZMW(detail.total_credit)}</td>
                  <td></td>
                </tr>
              </tfoot>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}
